/* eslint-disable prefer-destructuring */
import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { Box, Skeleton, Stack, useTheme } from '@mui/material';

import AuthStore from '@/stores/AuthStore';
import discoverService from '@/services/discoverService';
import FridgeDoorStore from '@/stores/FridgeDoorStore';
import WishlistStore from '@/stores/WishlistStore';
import { loggedActionsGuard } from '@/utils/loggedActionsGuard';
import { useResponsive } from '@/hooks/useResponsive';
import {
  EFilters,
  FilterSection,
} from './components/FilterSection/FilterSection';
import { GroupBanner } from './components/GroupBanner/GroupBanner';
import { FilterValues } from './constants/FilterValues';
import { SortValues } from './constants/SortValues';
import { RenderContent } from './components/RenderContent';

function isUserMember(userInfo: any, groupId: string) {
  if (!userInfo?.subscriptionGroups) return false;

  return userInfo.subscriptionGroups.some(
    (currentGroup) => currentGroup.id === groupId,
  );
}

function getFilterValues(
  oldFilters: Array<FilterValues>,
  incomingFilter: FilterValues,
) {
  if (
    oldFilters.includes(FilterValues.ALL) ||
    incomingFilter === FilterValues.ALL
  ) {
    return [incomingFilter];
  }
  if (oldFilters.includes(incomingFilter)) {
    const newFilters = oldFilters.filter((filter) => filter !== incomingFilter);
    if (!newFilters.length) return [FilterValues.ALL];
    return newFilters;
  }
  const newFilters = [...oldFilters, incomingFilter];
  const filterWithoutAll = JSON.parse(JSON.stringify(FilterValues));
  delete filterWithoutAll.ALL;
  const isAllChecked = Object.values(filterWithoutAll).reduce(
    (isChecked: boolean, filter: FilterValues): boolean =>
      Boolean(isChecked && newFilters.includes(filter)),
    true,
  );

  if (isAllChecked) return [FilterValues.ALL];

  return newFilters;
}

function GroupPageComponent({ groupProps, slug, sortBy, filter }): JSX.Element {
  const theme = useTheme();
  const { cmsUser, isLoggedIn } = AuthStore;
  const isMobile = useResponsive('down', 'md');
  const [isLoading, setIsLoading] = useState(false);
  const [group, setGroup] = useState(groupProps);
  const [commentsCollapsed, setCommentCollapsed] = useState(null);

  function handleToggleCollapse(collapsed, id) {
    setCommentCollapsed(collapsed ? id : null);
  }

  const getInitFilter = useCallback(() => {
    if (typeof filter === 'string') return [filter];
    if (Array.isArray(filter) && filter.length) return filter;
    return [FilterValues.ALL];
  }, [filter]);
  const [filteredBy, setFilteredBy] = useState<Array<FilterValues>>(
    getInitFilter,
  );

  const getInitSorting = useCallback(() => {
    if (!sortBy) {
      return SortValues.TRENDING;
    }
    return sortBy;
  }, [sortBy]);
  const [sortedBy, setSortedBy] = useState(getInitSorting);

  useEffect(() => {
    setGroup(groupProps);
    setSortedBy(getInitSorting);
    setFilteredBy(getInitFilter);

    // don't include getInitSorting and getInitFilter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupProps]);

  const [isMember, setIsMember] = useState(() =>
    isUserMember(cmsUser, groupProps?.groupId),
  );

  useEffect(() => {
    const newMemberState = isUserMember(cmsUser, groupProps?.groupId);
    setIsMember(newMemberState);
  }, [cmsUser, groupProps]);

  const villageSlug = FridgeDoorStore?.defaultVillage?.slug;

  const router = useRouter();

  const handleSortChange = useCallback(
    async function handleSortChange(sortValue: SortValues) {
      const { pathname, query } = router;
      setIsLoading(true);
      router.push(
        {
          pathname,
          query: { ...query, filter: filteredBy, sortBy: sortValue },
        },
        null,
        { shallow: true },
      );
      setSortedBy(sortValue);
      const newContent = await discoverService.getGroup(
        slug,
        [],
        filteredBy,
        sortValue === SortValues.TRENDING,
      );
      setGroup(newContent);
      setIsLoading(false);
    },
    [filteredBy, router, slug],
  );

  async function handleFilterChange(filterValue: FilterValues) {
    const { pathname, query } = router;

    setIsLoading(true);
    const newFilters = getFilterValues(filteredBy, filterValue);

    router.push(
      {
        pathname,
        query: { ...query, filter: newFilters, sortBy: sortedBy },
      },
      null,
      { shallow: true },
    );
    setFilteredBy(newFilters);

    const newContent = await discoverService.getGroup(
      slug,
      undefined,
      newFilters,
      sortedBy === SortValues.TRENDING,
    );
    setGroup(newContent);
    setIsLoading(false);
  }
  async function joinLeaveGroup() {
    setIsLoading(true);
    if (isMember) {
      await discoverService.leaveGroup({
        groupId: group.groupId,
        userId: cmsUser.id,
      });
    } else {
      await discoverService.joinGroup({
        groupId: group.groupId,
        userId: cmsUser.id,
      });
    }
    const newContent = await discoverService.getGroup(
      slug,
      undefined,
      filteredBy,
      sortedBy === SortValues.TRENDING,
    );
    await AuthStore.getCmsUser();
    setIsLoading(false);
    setGroup(newContent);
  }

  const handleJoinLeave = (loggedActionsGuard(
    isLoggedIn,
    false,
    joinLeaveGroup,
  ) as unknown) as MouseEventHandler;

  useEffect(() => {
    if (villageSlug) {
      WishlistStore.fetchWishlist(villageSlug);
    }
  }, [villageSlug]);

  const uniqueContentList: any[] = Array.isArray(group?.content)
    ? [...new Set(group.content)]
    : [];

  const [filtersOpened, setFiltersOpened] = useState<EFilters | null>(null);

  return (
    <div>
      <Box mb="30px">
        <GroupBanner
          image={group?.image}
          title={group?.name}
          subtitle={group?.description}
          users={group?.users}
          isMember={isMember}
          onJoinLeave={handleJoinLeave}
          isLoading={isLoading}
          group={group}
          slug={slug}
          filteredBy={filteredBy}
          sortedBy={sortedBy}
          setGroup={setGroup}
          filtersOpenedMob={filtersOpened}
          setFiltersOpenedMob={setFiltersOpened}
        >
          <FilterSection
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            filteredBy={filteredBy}
            sortedBy={sortedBy}
            filtersOpenedMob={filtersOpened}
            mobile
          />
        </GroupBanner>
      </Box>
      <Box>
        <Box mb={theme.spacing(6.5)}>
          <FilterSection
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            filteredBy={filteredBy}
            sortedBy={sortedBy}
            mobile={false}
          />
        </Box>

        <Stack mb="50px" gap={isMobile ? 3 : 7.5}>
          {isLoading
            ? [1, 2, 3].map((key) => (
                <Skeleton
                  key={key}
                  component={Box}
                  sx={{
                    borderRadius: '16px',
                    height: '300px',
                    transform: 'none',
                  }}
                />
              ))
            : uniqueContentList.map((content) => (
                <RenderContent
                  key={content.contentId}
                  content={content}
                  commentsCollapsed={commentsCollapsed}
                  handleToggleCollapse={handleToggleCollapse}
                />
              ))}
        </Stack>
      </Box>
    </div>
  );
}

export const GroupPage = observer(GroupPageComponent);

GroupPageComponent.defaultProps = {
  filteredBy: FilterValues.ALL,
  sortedBy: SortValues.TRENDING,
};
